import type { i18n as I18nInstance } from 'i18next';

import {
  DEFAULT_LANGUAGE,
  LanguageCode,
  SUPPORTED_LANGUAGE_CODES,
  TRANSLATION_API_URL,
} from './config';

const CACHE_PREFIX = 'i18nApiCache_';
const DEBOUNCE_MS = 400;
const NAMESPACE = 'translation';

type TranslationMap = Record<string, string>;

const loadCache = (lng: string): TranslationMap => {
  try {
    return JSON.parse(localStorage.getItem(CACHE_PREFIX + lng) || '{}');
  } catch {
    return {};
  }
};

const saveCache = (lng: string, cache: TranslationMap) => {
  try {
    localStorage.setItem(CACHE_PREFIX + lng, JSON.stringify(cache));
  } catch {
    /* storage may be full or unavailable; ignore */
  }
};

/**
 * Translate a single English string into the target language.
 * Returns null when no endpoint is configured or the request fails, so the
 * caller can safely keep the English fallback.
 */
async function translateText(
  text: string,
  target: LanguageCode
): Promise<string | null> {
  if (!TRANSLATION_API_URL) return null;
  try {
    const res = await fetch(TRANSLATION_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, source: DEFAULT_LANGUAGE, target }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    const translated = data?.translatedText ?? data?.translation ?? null;
    return typeof translated === 'string' && translated.length > 0
      ? translated
      : null;
  } catch {
    return null;
  }
}

/**
 * Wires an API-based translation fallback into the given i18n instance.
 * Missing keys for non-English languages are collected, translated in a
 * debounced batch, cached in localStorage, and injected back into i18next
 * (which re-renders bound components via the "added" event).
 */
export function registerApiFallback(i18n: I18nInstance) {
  // Seed previously cached translations so they are available immediately.
  SUPPORTED_LANGUAGE_CODES.filter((lng) => lng !== DEFAULT_LANGUAGE).forEach(
    (lng) => {
      const cache = loadCache(lng);
      if (Object.keys(cache).length > 0) {
        i18n.addResourceBundle(lng, NAMESPACE, cache, true, true);
      }
    }
  );

  const pending: Record<string, Set<string>> = {};
  let timer: ReturnType<typeof setTimeout> | undefined;

  const flush = async () => {
    timer = undefined;
    const languages = Object.keys(pending);
    for (const lng of languages) {
      const keys = Array.from(pending[lng] ?? []);
      delete pending[lng];
      if (keys.length === 0) continue;

      const cache = loadCache(lng);
      const resolved: TranslationMap = {};

      await Promise.all(
        keys.map(async (key) => {
          if (cache[key]) {
            resolved[key] = cache[key];
            return;
          }
          const source = i18n.getResource(
            DEFAULT_LANGUAGE,
            NAMESPACE,
            key
          ) as string | undefined;
          if (!source) return;
          const translated = await translateText(source, lng as LanguageCode);
          if (translated) {
            resolved[key] = translated;
            cache[key] = translated;
          }
        })
      );

      if (Object.keys(resolved).length > 0) {
        saveCache(lng, cache);
        i18n.addResourceBundle(lng, NAMESPACE, resolved, true, true);
      }
    }
  };

  const schedule = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      void flush();
    }, DEBOUNCE_MS);
  };

  i18n.on('missingKey', (lngs, _ns, key) => {
    const lng = Array.isArray(lngs) ? lngs[0] : lngs;
    if (!lng || lng === DEFAULT_LANGUAGE) return;
    if (!SUPPORTED_LANGUAGE_CODES.includes(lng as LanguageCode)) return;
    (pending[lng] ??= new Set<string>()).add(key);
    schedule();
  });
}
